import type { Editor } from "@tiptap/core";

// Thin dispatch table, one line per old toolbar action — mirrors the shape of the old
// examinRichEditor.command(hostId, command, value) API so the Blazor side's existing
// Command("bold")/Command("formatBlock","blockquote") call sites need no changes (see the
// plan's §4 mapping table). Adding/removing a toolbar button only ever touches this file.
export const COMMANDS: Record<string, (editor: Editor, value?: string) => boolean> = {
  bold: (e) => e.chain().focus().toggleBold().run(),
  italic: (e) => e.chain().focus().toggleItalic().run(),
  underline: (e) => e.chain().focus().toggleUnderline().run(),
  strike: (e) => e.chain().focus().toggleStrike().run(),
  highlight: (e) => e.chain().focus().toggleHighlight().run(),
  subscript: (e) => e.chain().focus().toggleSubscript().run(),
  superscript: (e) => e.chain().focus().toggleSuperscript().run(),
  justifyLeft: (e) => e.chain().focus().setTextAlign("left").run(),
  justifyCenter: (e) => e.chain().focus().setTextAlign("center").run(),
  justifyRight: (e) => e.chain().focus().setTextAlign("right").run(),
  insertUnorderedList: (e) => e.chain().focus().toggleBulletList().run(),
  insertOrderedList: (e) => e.chain().focus().toggleOrderedList().run(),
  removeFormat: (e) => e.chain().focus().unsetAllMarks().clearNodes().run(),
  color: (e, value) =>
    value
      ? (e.chain().focus() as any).setFontColor(value).run()
      : (e.chain().focus() as any).unsetFontColor().run(),
  undo: (e) => e.chain().focus().undo().run(),
  redo: (e) => e.chain().focus().redo().run(),
  formatBlock: (e, value) => {
    if (value === "blockquote") return e.chain().focus().toggleBlockquote().run();
    const headingMatch = /^h([1-3])$/.exec(value ?? "");
    if (headingMatch) {
      const level = Number(headingMatch[1]) as 1 | 2 | 3;
      return e.chain().focus().toggleHeading({ level }).run();
    }
    return e.chain().focus().setParagraph().run();
  },
};

// Mirrors COMMANDS above, one entry per toolbar control that has an on/off state, keyed by
// the same name the Blazor side already sends for the click. Keeping both tables keyed alike
// is the point: a button's pressed look and its action can never drift apart. Blockquote and
// the heading levels are keyed by what they produce rather than by "formatBlock", since that
// one command backs four different buttons.
export const ACTIVE_STATES: Record<string, (editor: Editor) => boolean> = {
  bold: (e) => e.isActive("bold"),
  italic: (e) => e.isActive("italic"),
  underline: (e) => e.isActive("underline"),
  strike: (e) => e.isActive("strike"),
  highlight: (e) => e.isActive("highlight"),
  subscript: (e) => e.isActive("subscript"),
  superscript: (e) => e.isActive("superscript"),
  justifyLeft: (e) => e.isActive({ textAlign: "left" }),
  justifyCenter: (e) => e.isActive({ textAlign: "center" }),
  justifyRight: (e) => e.isActive({ textAlign: "right" }),
  insertUnorderedList: (e) => e.isActive("bulletList"),
  insertOrderedList: (e) => e.isActive("orderedList"),
  blockquote: (e) => e.isActive("blockquote"),
  codeBlock: (e) => e.isActive("codeBlock"),
  table: (e) => e.isActive("table"),
  panel: (e) => e.isActive("panel"),
  h1: (e) => e.isActive("heading", { level: 1 }),
  h2: (e) => e.isActive("heading", { level: 2 }),
  h3: (e) => e.isActive("heading", { level: 3 }),
};

/// Names of every state currently on. An extension that is not loaded makes isActive throw
/// rather than return false, so each probe is guarded — one missing extension must not take
/// the whole toolbar's feedback down with it.
export function activeCommands(editor: Editor): string[] {
  const on: string[] = [];
  for (const key of Object.keys(ACTIVE_STATES)) {
    try {
      if (ACTIVE_STATES[key](editor)) on.push(key);
    } catch {
      /* extension not loaded in this variant */
    }
  }
  return on;
}

export function runCommand(editor: Editor, command: string, value?: string): boolean {
  const fn = COMMANDS[command];
  return fn ? fn(editor, value) : false;
}

export function changeFontSize(editor: Editor, delta: number): boolean {
  const current = Number(editor.getAttributes("fontSize").size) || 3;
  const next = Math.min(7, Math.max(1, current + (delta < 0 ? -1 : 1)));
  return (editor.commands as any).setFontSize(String(next));
}

export function tableCommand(editor: Editor, command: string): boolean {
  const chain = editor.chain().focus();
  switch (command) {
    case "add-row":
      return chain.addRowAfter().run();
    case "delete-row":
      return chain.deleteRow().run();
    case "add-column":
      return chain.addColumnAfter().run();
    case "delete-column":
      return chain.deleteColumn().run();
    default:
      return false;
  }
}
